import { useCallback, useEffect, useRef, useState } from 'react'

import { logger } from '@/lib/logger'
import { APP_BUILD_ID, fetchRemoteVersion } from '@/lib/version'

const CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes
const MIN_CHECK_GAP = 30 * 1000 // 30 seconds between checks

let lastCheckTime = 0

export function useVersionCheck() {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false)
  const dismissedVersionRef = useRef<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval>>(undefined)

  const checkForNewVersion = useCallback(async () => {
    const now = Date.now()
    if (now - lastCheckTime < MIN_CHECK_GAP) return
    lastCheckTime = now

    const remoteVersion = await fetchRemoteVersion()

    if (
      remoteVersion &&
      remoteVersion !== APP_BUILD_ID &&
      remoteVersion !== dismissedVersionRef.current
    ) {
      logger.info(
        'New app version detected',
        { localVersion: APP_BUILD_ID, remoteVersion },
        'version',
      )
      setIsNewVersionAvailable(true)
    }
  }, [])

  const dismiss = useCallback(() => {
    dismissedVersionRef.current = null
    fetchRemoteVersion().then((v) => {
      if (v) dismissedVersionRef.current = v
    })
    setIsNewVersionAvailable(false)
  }, [])

  useEffect(() => {
    if (import.meta.env.DEV) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForNewVersion()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    intervalRef.current = setInterval(checkForNewVersion, CHECK_INTERVAL)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(intervalRef.current)
    }
  }, [checkForNewVersion])

  return { isNewVersionAvailable, dismiss }
}
